import { ChangeDetectionStrategy, Component, computed, effect, inject, input, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { LookupStore } from '@core/lookup/lookup.store';
import { LOOKUP_CODES } from '@core/lookup/lookup-codes';
import { LookupResponse } from '@core/models/common.model';
import { PermissionService } from '@core/services/permission.service';
import { problemsFor } from '@features/intervention/assessment/cia/cia-lookups';
import { Permission } from '@shared/enums/permission.enum';
import { LookupSelectComponent } from '@shared/components/lookup-select/lookup-select.component';
import { SharedMaterialUIModule } from '@shared/modules/shared-material-ui.module';
import { nullIfBlank } from '@shared/utils/payload-utils';
import { InterventionStore } from '../../../intervention.store';
import { PresentingProblemCodingRequest } from '../../../intervention.model';

// The coded presenting problem of the case (the "Presenting Problem" step, before the clinical assessment): a primary problem, and an optional
// secondary one, each a problem of its cluster. It is recorded on the intervention so queues, dashboards and the member's history read the case.
@Component({
  selector: 'presenting-problem-panel',
  imports: [ReactiveFormsModule, LookupSelectComponent, ...SharedMaterialUIModule],
  templateUrl: './presenting-problem-panel.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class PresentingProblemPanelComponent {
  protected readonly store = inject(InterventionStore);
  private readonly lookupStore = inject(LookupStore);
  private readonly permissionService = inject(PermissionService);
  private readonly formBuilder = inject(FormBuilder);

  readonly interventionId = input.required<string>();
  readonly interventionStatus = input<string | null>(null);

  protected readonly editing = signal(false);

  protected readonly clusters = computed(() => this.lookupStore.items(LOOKUP_CODES.problemCluster));
  private readonly lists = computed(() => ({
    problemClusters: this.lookupStore.items(LOOKUP_CODES.problemCluster),
    problems: this.lookupStore.items(LOOKUP_CODES.problem),
  }));

  protected readonly canEdit = computed(
    () => this.permissionService.hasPermission(Permission.InterventionsUpdate) && this.interventionStatus() !== 'Closed',
  );

  protected readonly isEmpty = computed(() => !this.store.presentingProblem()?.primary.problemId);

  protected readonly form = this.formBuilder.nonNullable.group({
    primaryClusterId: [''],
    primaryProblemId: [''],
    secondaryClusterId: [''],
    secondaryProblemId: [''],
  });

  constructor() {
    this.lookupStore.load([LOOKUP_CODES.problemCluster, LOOKUP_CODES.problem]);
    effect(() => this.store.loadPresentingProblem(this.interventionId()));
  }

  // The problems of one cluster, for the problem pick that follows the cluster pick.
  protected problemsOf(clusterId: string): LookupResponse[] {
    return problemsFor(this.lists(), clusterId);
  }

  // Choosing another cluster clears a problem that does not belong to it.
  protected clusterChanged(cluster: 'primary' | 'secondary'): void {
    const controls = this.form.controls;
    const clusterControl = cluster === 'primary' ? controls.primaryClusterId : controls.secondaryClusterId;
    const problemControl = cluster === 'primary' ? controls.primaryProblemId : controls.secondaryProblemId;

    if (problemControl.value && !this.problemsOf(clusterControl.value).some((item) => item.id === problemControl.value)) {
      problemControl.setValue('');
    }
  }

  protected edit(): void {
    const saved = this.store.presentingProblem();
    this.form.reset({
      primaryClusterId: saved?.primary.clusterId ?? '',
      primaryProblemId: saved?.primary.problemId ?? '',
      secondaryClusterId: saved?.secondary.clusterId ?? '',
      secondaryProblemId: saved?.secondary.problemId ?? '',
    });
    this.editing.set(true);
  }

  protected cancel(): void {
    this.editing.set(false);
  }

  protected save(): void {
    const value = this.form.getRawValue();
    const request: PresentingProblemCodingRequest = {
      primary: { clusterId: nullIfBlank(value.primaryClusterId), problemId: nullIfBlank(value.primaryProblemId) },
      secondary: { clusterId: nullIfBlank(value.secondaryClusterId), problemId: nullIfBlank(value.secondaryProblemId) },
    };

    this.store.savePresentingProblem(this.interventionId(), request, () => this.editing.set(false));
  }
}
